'use client';

import React, { useState } from 'react';
import { ADR, ADRStatus, ExtendedArchiMateDiagram } from '@/lib/archimate/extended-types';
import {
  createADR,
  updateADRStatus,
  getADRsByStatus,
  getADRTimeline,
  generateADRMarkdown,
  exportADRsToMarkdown,
  getADRTemplate,
  searchADRs,
} from '@/lib/archimate/adr-utils';
import { X, Plus, Download, Search, FileText, Clock } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface ADRPanelProps {
  diagram: ExtendedArchiMateDiagram;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
}

type ViewMode = 'list' | 'timeline' | 'create' | 'view';

export default function ADRPanel({ diagram, onUpdate, onClose }: ADRPanelProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedADR, setSelectedADR] = useState<ADR | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<ADRStatus | 'all'>('all');
  const [showHelp, setShowHelp] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    context: '',
    decision: '',
    consequences: '',
    alternatives: '',
  });

  const [selectedTemplate, setSelectedTemplate] = useState<'nygard' | 'madr' | 'y-statement' | 'blank'>('blank');

  const filteredADRs = React.useMemo(() => {
    let adrs = diagram.adrs || [];

    // Apply search
    if (searchQuery) {
      adrs = searchADRs(diagram, searchQuery);
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      adrs = getADRsByStatus(diagram, filterStatus);
    }

    return adrs;
  }, [diagram, searchQuery, filterStatus]);

  const handleCreateADR = () => {
    const adr = createADR(
      formData.title,
      formData.context,
      formData.decision,
      formData.consequences
    );

    if (formData.alternatives) {
      adr.alternatives = formData.alternatives;
    }

    const updatedDiagram = {
      ...diagram,
      adrs: [...(diagram.adrs || []), adr],
    };

    onUpdate(updatedDiagram);
    setViewMode('list');
    resetForm();
  };

  const handleUpdateStatus = (adr: ADR, status: ADRStatus) => {
    const updatedADR = updateADRStatus(adr, status);
    const updatedDiagram = {
      ...diagram,
      adrs: diagram.adrs.map((a) => (a.id === adr.id ? updatedADR : a)),
    };
    onUpdate(updatedDiagram);
  };

  const handleDeleteADR = (adrId: string) => {
    const updatedDiagram = {
      ...diagram,
      adrs: diagram.adrs.filter((a) => a.id !== adrId),
    };
    onUpdate(updatedDiagram);
    setSelectedADR(null);
    setViewMode('list');
  };

  const handleExport = () => {
    const markdown = exportADRsToMarkdown(diagram);
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-ADRs.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleApplyTemplate = () => {
    if (selectedTemplate === 'blank') return;

    const template = getADRTemplate(selectedTemplate);
    setFormData({
      ...formData,
      context: template.context,
      decision: template.decision,
      consequences: template.consequences,
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      context: '',
      decision: '',
      consequences: '',
      alternatives: '',
    });
    setSelectedTemplate('blank');
  };

  const getStatusColor = (status: ADRStatus) => {
    switch (status) {
      case 'proposed':
        return 'bg-blue-500';
      case 'accepted':
        return 'bg-green-500';
      case 'deprecated':
        return 'bg-yellow-500';
      case 'superseded':
        return 'bg-gray-500';
    }
  };

  const getStatusCount = (status: ADRStatus) => {
    return getADRsByStatus(diagram, status).length;
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-[600px] bg-background border-l border-border flex flex-col shadow-2xl z-50 animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Architecture Decision Records</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-cream/10 rounded transition-colors"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="adr"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Toolbar */}
      <div className="p-4 border-b border-border space-y-3">
        {/* View Mode Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'list' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            List
          </button>
          <button
            onClick={() => setViewMode('timeline')}
            className={`px-4 py-2 rounded transition-colors flex items-center gap-2 ${
              viewMode === 'timeline' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            <Clock className="w-4 h-4" />
            Timeline
          </button>
          <button
            onClick={() => {
              setViewMode('create');
              resetForm();
            }}
            className="ml-auto px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New ADR
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>

        {/* Search and Filter */}
        {(viewMode === 'list' || viewMode === 'timeline') && (
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-tertiary" />
              <input
                type="text"
                placeholder="Search ADRs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as ADRStatus | 'all')}
              className="px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
            >
              <option value="all">All Status ({diagram.adrs?.length || 0})</option>
              <option value="proposed">Proposed ({getStatusCount('proposed')})</option>
              <option value="accepted">Accepted ({getStatusCount('accepted')})</option>
              <option value="deprecated">Deprecated ({getStatusCount('deprecated')})</option>
              <option value="superseded">Superseded ({getStatusCount('superseded')})</option>
            </select>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* List View */}
        {viewMode === 'list' && (
          <div className="space-y-2">
            {filteredADRs.length === 0 ? (
              <div className="text-center py-12 text-foreground-tertiary">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No ADRs found</p>
                <button
                  onClick={() => setViewMode('create')}
                  className="mt-4 px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover"
                >
                  Create First ADR
                </button>
              </div>
            ) : (
              filteredADRs.map((adr) => (
                <div
                  key={adr.id}
                  onClick={() => {
                    setSelectedADR(adr);
                    setViewMode('view');
                  }}
                  className="p-4 bg-cream/5 hover:bg-cream/10 border border-border rounded cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-foreground">{adr.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs text-white ${getStatusColor(adr.status)}`}>
                      {adr.status}
                    </span>
                  </div>
                  <p className="text-sm text-foreground-secondary mb-2 line-clamp-2">{adr.context}</p>
                  <div className="flex items-center gap-4 text-xs text-foreground-tertiary">
                    <span>{adr.date}</span>
                    {adr.author && <span>by {adr.author}</span>}
                    {adr.relatedElements.length > 0 && (
                      <span>{adr.relatedElements.length} linked elements</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Timeline View */}
        {viewMode === 'timeline' && (
          <div className="space-y-4">
            {getADRTimeline(diagram).map((adr, index) => (
              <div key={adr.id} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(adr.status)}`} />
                  {index < diagram.adrs.length - 1 && (
                    <div className="w-0.5 flex-1 bg-cream/20 my-1" />
                  )}
                </div>
                <div
                  onClick={() => {
                    setSelectedADR(adr);
                    setViewMode('view');
                  }}
                  className="flex-1 pb-4 cursor-pointer"
                >
                  <div className="p-4 bg-cream/5 hover:bg-cream/10 border border-border rounded transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-foreground-tertiary">{adr.date}</span>
                      <span className={`px-2 py-0.5 rounded text-xs text-white ${getStatusColor(adr.status)}`}>
                        {adr.status}
                      </span>
                    </div>
                    <h3 className="font-bold text-foreground mb-1">{adr.title}</h3>
                    <p className="text-sm text-foreground-secondary line-clamp-2">{adr.decision}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create View */}
        {viewMode === 'create' && (
          <div className="space-y-4">
            {/* Template Selector */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Template</label>
              <div className="flex gap-2">
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value as any)}
                  className="flex-1 px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                >
                  <option value="blank">Blank</option>
                  <option value="nygard">Michael Nygard Format</option>
                  <option value="madr">MADR Format</option>
                  <option value="y-statement">Y-Statement</option>
                </select>
                <button
                  onClick={handleApplyTemplate}
                  disabled={selectedTemplate === 'blank'}
                  className="px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Use Microservices Architecture"
                className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent"
              />
            </div>

            {/* Context */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Context *</label>
              <textarea
                value={formData.context}
                onChange={(e) => setFormData({ ...formData, context: e.target.value })}
                placeholder="What is the issue that we're seeing that is motivating this decision?"
                rows={4}
                className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent resize-none"
              />
            </div>

            {/* Decision */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Decision *</label>
              <textarea
                value={formData.decision}
                onChange={(e) => setFormData({ ...formData, decision: e.target.value })}
                placeholder="What is the change that we're proposing and/or doing?"
                rows={4}
                className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent resize-none"
              />
            </div>

            {/* Consequences */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Consequences *</label>
              <textarea
                value={formData.consequences}
                onChange={(e) => setFormData({ ...formData, consequences: e.target.value })}
                placeholder="What becomes easier or more difficult to do because of this change?"
                rows={4}
                className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent resize-none"
              />
            </div>

            {/* Alternatives */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Alternatives Considered</label>
              <textarea
                value={formData.alternatives}
                onChange={(e) => setFormData({ ...formData, alternatives: e.target.value })}
                placeholder="What other options were considered?"
                rows={3}
                className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <button
                onClick={handleCreateADR}
                disabled={!formData.title || !formData.context || !formData.decision || !formData.consequences}
                className="flex-1 px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create ADR
              </button>
              <button
                onClick={() => {
                  setViewMode('list');
                  resetForm();
                }}
                className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* View/Edit Selected ADR */}
        {viewMode === 'view' && selectedADR && (
          <div className="space-y-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-2">{selectedADR.title}</h3>
                <div className="flex items-center gap-4 text-sm text-foreground-secondary">
                  <span>{selectedADR.date}</span>
                  {selectedADR.author && <span>by {selectedADR.author}</span>}
                </div>
              </div>
              <span className={`px-3 py-1 rounded text-sm text-white ${getStatusColor(selectedADR.status)}`}>
                {selectedADR.status}
              </span>
            </div>

            <div className="prose prose-invert max-w-none">
              <h4 className="text-lg font-bold text-accent">Context</h4>
              <p className="text-foreground/90 whitespace-pre-wrap">{selectedADR.context}</p>

              <h4 className="text-lg font-bold text-accent mt-6">Decision</h4>
              <p className="text-foreground/90 whitespace-pre-wrap">{selectedADR.decision}</p>

              <h4 className="text-lg font-bold text-accent mt-6">Consequences</h4>
              <p className="text-foreground/90 whitespace-pre-wrap">{selectedADR.consequences}</p>

              {selectedADR.alternatives && (
                <>
                  <h4 className="text-lg font-bold text-accent mt-6">Alternatives Considered</h4>
                  <p className="text-foreground/90 whitespace-pre-wrap">{selectedADR.alternatives}</p>
                </>
              )}
            </div>

            {/* Status Actions */}
            <div className="border-t border-border pt-4">
              <label className="block text-sm font-medium text-foreground mb-2">Update Status</label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleUpdateStatus(selectedADR, 'proposed')}
                  disabled={selectedADR.status === 'proposed'}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  Proposed
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedADR, 'accepted')}
                  disabled={selectedADR.status === 'accepted'}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  Accepted
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedADR, 'deprecated')}
                  disabled={selectedADR.status === 'deprecated'}
                  className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  Deprecated
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedADR, 'superseded')}
                  disabled={selectedADR.status === 'superseded'}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  Superseded
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t border-border">
              <button
                onClick={() => {
                  const markdown = generateADRMarkdown(selectedADR);
                  const blob = new Blob([markdown], { type: 'text/markdown' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `ADR-${selectedADR.title}.md`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export Markdown
              </button>
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to delete this ADR?')) {
                    handleDeleteADR(selectedADR.id);
                  }
                }}
                className="ml-auto px-4 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
              >
                Delete
              </button>
              <button
                onClick={() => setViewMode('list')}
                className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20"
              >
                Back to List
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
